import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  LogOut, 
  Save, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  Palette,
  Package,
  FolderOpen,
  Home,
  ExternalLink,
  ShoppingBag,
  Users,
  Clock,
  MapPin,
  CreditCard,
  Download,
  ChevronDown,
  ChevronUp,
  BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import ProductsBulkTable from '../components/admin/ProductsBulkTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lee la orientación EXIF de un ArrayBuffer de imagen JPEG
const getExifOrientation = (arrayBuffer) => {
  try {
    const view = new DataView(arrayBuffer);
    if (view.getUint16(0, false) !== 0xFFD8) return 1;
    let offset = 2;
    while (offset < view.byteLength) {
      const marker = view.getUint16(offset, false);
      if (marker === 0xFFE1) {
        if (view.getUint32(offset + 2, false) !== 0x45786966) return 1;
        const little = view.getUint16(offset + 8, false) === 0x4949;
        const ifdOffset = offset + 8 + view.getUint32(offset + 12, little);
        const tags = view.getUint16(ifdOffset, little);
        for (let i = 0; i < tags; i++) {
          if (view.getUint16(ifdOffset + 2 + i * 12, little) === 0x0112) {
            return view.getUint16(ifdOffset + 2 + i * 12 + 8, little);
          }
        }
      } else if ((marker & 0xFF00) !== 0xFF00) {
        break;
      }
      offset += 2 + view.getUint16(offset + 2, false);
    }
  } catch (e) { /* ignorar errores de parseo */ }
  return 1;
};

// Corrige la orientación EXIF dibujando en canvas y devuelve un File corregido
const correctImageOrientation = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const orientation = getExifOrientation(ev.target.result);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        // Para orientaciones 5-8 el ancho y alto se intercambian
        if (orientation >= 5) { canvas.width = h; canvas.height = w; }
        else { canvas.width = w; canvas.height = h; }
        switch (orientation) {
          case 2: ctx.transform(-1, 0, 0, 1, w, 0); break;
          case 3: ctx.transform(-1, 0, 0, -1, w, h); break;
          case 4: ctx.transform(1, 0, 0, -1, 0, h); break;
          case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;
          case 6: ctx.transform(0, 1, -1, 0, h, 0); break;
          case 7: ctx.transform(0, -1, -1, 0, h, w); break;
          case 8: ctx.transform(0, -1, 1, 0, 0, w); break;
          default: break;
        }
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(img.src);
        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name, { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.92);
      };
      img.src = URL.createObjectURL(file);
    };
    reader.readAsArrayBuffer(file);
  });
};

const AdminPanel = () => {
  const { isAuthenticated, isBusinessAdmin, logout, businessId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeSection, setActiveSection] = useState('orders');
  const [availableFonts, setAvailableFonts] = useState([
    { name: 'Playfair Display' }, { name: 'Montserrat' }, { name: 'Lora' },
    { name: 'Poppins' }, { name: 'Roboto' }, { name: 'Open Sans' }
  ]);

  // Orders & Customers
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersLoadingMore, setOrdersLoadingMore] = useState(false);
  const ORDERS_PAGE_SIZE = 50;
  const [customers, setCustomers] = useState([]);
  const [reportsSummary, setReportsSummary] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  // Selección múltiple para borrado
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [selectedCustomerIds, setSelectedCustomerIds] = useState(new Set());
  const [selectedCategoryIds, setSelectedCategoryIds] = useState(new Set());
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());

  // Settings
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState({
    accepts_delivery: true,
    accepts_takeaway: true,
    min_order_amount: 0,
    estimated_delivery_time: '30-45 min',
    payment_methods: ['cash', 'transfer', 'mercadopago'],
    mercadopago_public_key: '',
    mercadopago_access_token: ''
  });
  const [businessHours, setBusinessHours] = useState([]);
  const [deliveryZones, setDeliveryZones] = useState([]);

  // Category Dialog
  const [categoryDialog, setCategoryDialog] = useState({ open: false, data: null });
  const [categoryForm, setCategoryForm] = useState({ name: '', order: 0, hidden: false });

  // Product Dialog
  const [productDialog, setProductDialog] = useState({ open: false, data: null });
  const [productsView, setProductsView] = useState('cards'); // 'cards' | 'bulk'
  const [productForm, setProductForm] = useState({
    category_id: '',
    name: '',
    description: '',
    price: 0,
    image_url: '',
    available: true,
    is_popular: false,
    discount_price: null,
    stock: null  // null = unlimited
  });

  // Branding Form
  const [brandingForm, setBrandingForm] = useState({
    name: '',
    whatsapp: '',
    hero_image: '',
    logo_url: '',
    theme_preset: 'modern',
    primary_color: '#000000',
    accent_color: '#e9ff60',
    background_color: '#0D0D0D',
    text_color: '#FFFFFF',
    card_color: '#262626',
    font_heading: 'Playfair Display',
    font_body: 'Roboto',
    custom_button_text: '',
    custom_button_url: '',
    social_instagram: '',
    social_facebook: ''
  });

  useEffect(() => {
    if (isAuthenticated && isBusinessAdmin && businessId) {
      loadData();
      loadFonts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isBusinessAdmin, businessId]);

  const loadFonts = async () => {
    try {
      const [fontsRes, cssRes] = await Promise.all([
        axios.get(`${API}/fonts`),
        axios.get(`${API}/fonts/stylesheet`)
      ]);
      setAvailableFonts(fontsRes.data);
      // Inject custom font CSS
      if (cssRes.data.css) {
        let styleEl = document.getElementById('custom-fonts-style');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'custom-fonts-style';
          document.head.appendChild(styleEl);
        }
        styleEl.textContent = cssRes.data.css;
      }
    } catch (e) {
      // silent
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [businessRes, categoriesRes, productsRes, ordersRes, customersRes, reportsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/me`),
        axios.get(`${API}/admin/categories`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/products`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/orders?skip=0&limit=${ORDERS_PAGE_SIZE}`).catch(() => ({ data: { orders: [], total: 0 } })),
        axios.get(`${API}/admin/customers`).catch(() => ({ data: { customers: [] } })),
        axios.get(`${API}/admin/reports/summary`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/settings`).catch(() => ({ data: null }))
      ]);

      const currentBusiness = businessRes.data;
      setBusiness(currentBusiness);
      setBrandingForm({
        name: currentBusiness.name,
        whatsapp: currentBusiness.whatsapp,
        hero_image: currentBusiness.hero_image || '',
        logo_url: currentBusiness.logo_url || '',
        theme_preset: currentBusiness.theme_preset || 'modern',
        primary_color: currentBusiness.primary_color || '#000000',
        accent_color: currentBusiness.accent_color || '#e9ff60',
        background_color: currentBusiness.background_color || '#0D0D0D',
        text_color: currentBusiness.text_color || '#FFFFFF',
        card_color: currentBusiness.card_color || '#262626',
        font_heading: currentBusiness.font_heading || 'Playfair Display',
        font_body: currentBusiness.font_body || 'Roboto',
        custom_button_text: currentBusiness.custom_button_text || '',
        custom_button_url: currentBusiness.custom_button_url || '',
        social_instagram: currentBusiness.social_instagram || '',
        social_facebook: currentBusiness.social_facebook || ''
      });
      
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);
      setOrders((ordersRes.data.orders || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setOrdersTotal(ordersRes.data.total || (ordersRes.data.orders || []).length);
      setCustomers((customersRes.data.customers || []).sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)));
      setReportsSummary(reportsRes.data);
      
      if (settingsRes.data) {
        setSettings(settingsRes.data);
        setSettingsForm({
          accepts_delivery: settingsRes.data.accepts_delivery ?? true,
          accepts_takeaway: settingsRes.data.accepts_takeaway ?? true,
          min_order_amount: settingsRes.data.min_order_amount ?? 0,
          estimated_delivery_time: settingsRes.data.estimated_delivery_time || '30-45 min',
          payment_methods: settingsRes.data.payment_methods || ['cash', 'transfer', 'mercadopago'],
          mercadopago_public_key: settingsRes.data.mercadopago_public_key || '',
          mercadopago_access_token: settingsRes.data.mercadopago_access_token || ''
        });
        setBusinessHours(settingsRes.data.business_hours || []);
        setDeliveryZones(settingsRes.data.delivery_zones || []);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
      toast.error('Error al cargar datos del negocio');
    }
  };

  const loadMoreOrders = async () => {
    if (ordersLoadingMore) return;
    setOrdersLoadingMore(true);
    try {
      const res = await axios.get(`${API}/admin/orders?skip=${orders.length}&limit=${ORDERS_PAGE_SIZE}`);
      const incoming = res.data.orders || [];
      // Append sin duplicar por id
      setOrders(prev => {
        const existingIds = new Set(prev.map(o => o.id));
        const toAdd = incoming.filter(o => !existingIds.has(o.id));
        return [...prev, ...toAdd].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      });
      setOrdersTotal(res.data.total || (orders.length + incoming.length));
    } catch (error) {
      toast.error('Error al cargar más pedidos');
    } finally {
      setOrdersLoadingMore(false);
    }
  };

  const handleImageUpload = async (file, field) => {
    if (!file) return;
    try {
      // Corregir orientación EXIF antes de subir (fotos de móvil)
      const correctedFile = await correctImageOrientation(file);
      const formData = new FormData();
      formData.append('file', correctedFile);
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (field === 'product') {
        setProductForm(prev => ({ ...prev, image_url: response.data.url }));
      } else {
        setBrandingForm(prev => ({ ...prev, [field]: response.data.url }));
      }
      toast.success('Imagen cargada');
    } catch (error) {
      toast.error('Error al cargar imagen');
    }
  };

  const handleSaveBranding = async () => {
    try {
      await axios.put(`${API}/business/${businessId}`, brandingForm);
      toast.success('Configuración actualizada');
      loadData();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleSaveCategory = async () => {
    try {
      if (categoryDialog.data) {
        await axios.put(`${API}/categories/${categoryDialog.data.id}`, categoryForm);
        toast.success('Categoría actualizada');
      } else {
        await axios.post(`${API}/categories`, categoryForm);
        toast.success('Categoría creada');
      }
      setCategoryDialog({ open: false, data: null });
      setCategoryForm({ name: '', order: 0, hidden: false });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar categoría');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('¿Eliminar esta categoría y todos sus productos?')) return;
    try {
      await axios.delete(`${API}/categories/${id}`);
      toast.success('Categoría eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleSaveProduct = async () => {
    try {
      const productData = { ...productForm, discount_price: productForm.discount_price || null };
      if (productDialog.data) {
        await axios.put(`${API}/products/${productDialog.data.id}`, productData);
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API}/products`, productData);
        toast.success('Producto creado');
      }
      setProductDialog({ open: false, data: null });
      setProductForm({ category_id: '', name: '', description: '', price: 0, image_url: '', available: true, is_popular: false, discount_price: null, stock: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar producto');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success('Producto eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`);
      toast.success('Estado actualizado');
      loadData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const toggleOrderSelection = (id) => {
    setSelectedOrderIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllOrders = () => {
    if (selectedOrderIds.size === orders.length) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(orders.map(o => o.id)));
    }
  };

  const handleBulkDeleteOrders = async () => {
    if (selectedOrderIds.size === 0) return;
    if (!window.confirm(`¿Eliminar ${selectedOrderIds.size} pedido(s) seleccionado(s)? Esta acción no se puede deshacer.`)) return;
    try {
      const res = await axios.post(`${API}/admin/orders/bulk-delete`, { order_ids: Array.from(selectedOrderIds) });
      toast.success(res.data.message);
      setSelectedOrderIds(new Set());
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar pedidos');
    }
  };

  const toggleCustomerSelection = (id) => {
    setSelectedCustomerIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllCustomers = () => {
    if (selectedCustomerIds.size === customers.length) {
      setSelectedCustomerIds(new Set());
    } else {
      setSelectedCustomerIds(new Set(customers.map(c => c.id)));
    }
  };

  const handleBulkDeleteCustomers = async () => {
    if (selectedCustomerIds.size === 0) return;
    if (!window.confirm(`¿Eliminar ${selectedCustomerIds.size} cliente(s) y sus pedidos asociados? Esta acción no se puede deshacer.`)) return;
    try {
      const res = await axios.post(`${API}/admin/customers/bulk-delete`, { customer_ids: Array.from(selectedCustomerIds) });
      toast.success(res.data.message);
      setSelectedCustomerIds(new Set());
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar clientes');
    }
  };

  const toggleCategorySelection = (id) => {
    setSelectedCategoryIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllCategories = () => {
    if (selectedCategoryIds.size === categories.length) {
      setSelectedCategoryIds(new Set());
    } else {
      setSelectedCategoryIds(new Set(categories.map(c => c.id)));
    }
  };

  const handleBulkDeleteCategories = async () => {
    if (selectedCategoryIds.size === 0) return;
    if (!window.confirm(`¿Eliminar ${selectedCategoryIds.size} categoría(s) y todos sus productos? Esta acción no se puede deshacer.`)) return;
    try {
      const res = await axios.post(`${API}/admin/categories/bulk-delete`, { category_ids: Array.from(selectedCategoryIds) });
      toast.success(res.data.message);
      setSelectedCategoryIds(new Set());
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar categorías');
    }
  };

  const toggleProductSelection = (id) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllProducts = () => {
    if (selectedProductIds.size === products.length) {
      setSelectedProductIds(new Set());
    } else {
      setSelectedProductIds(new Set(products.map(p => p.id)));
    }
  };

  const handleBulkDeleteProducts = async () => {
    if (selectedProductIds.size === 0) return;
    if (!window.confirm(`¿Eliminar ${selectedProductIds.size} producto(s) seleccionado(s)? Esta acción no se puede deshacer.`)) return;
    try {
      const res = await axios.post(`${API}/admin/products/bulk-delete`, { product_ids: Array.from(selectedProductIds) });
      toast.success(res.data.message);
      setSelectedProductIds(new Set());
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar productos');
    }
  };



  const handleExportOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/reports/export`);
      const blob = new Blob([response.data.content], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = response.data.filename;
      link.click();
      toast.success(`Exportados ${response.data.total_records} registros`);
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const handleViewCustomerOrders = async (customer) => {
    try {
      const response = await axios.get(`${API}/admin/customers/${customer.id}/orders`);
      setSelectedCustomer(customer);
      setCustomerOrders(response.data.orders);
    } catch (error) {
      toast.error('Error al cargar historial');
    }
  };

  const handleExportCustomers = async () => {
    try {
      const response = await axios.get(`${API}/admin/customers/export`);
      const { filename, content } = response.data;
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Clientes exportados');
    } catch (error) {
      toast.error('Error al exportar clientes');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, {
        ...settingsForm,
        business_hours: businessHours,
        delivery_zones: deliveryZones
      });
      toast.success('Configuración guardada');
      loadData();
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  const updateBusinessHour = (index, field, value) => {
    const updated = [...businessHours];
    updated[index] = { ...updated[index], [field]: value };
    setBusinessHours(updated);
  };

  const addHourRange = (index) => {
    const updated = [...businessHours];
    if (!updated[index].extra_ranges) updated[index] = { ...updated[index], extra_ranges: [] };
    updated[index] = { ...updated[index], extra_ranges: [...(updated[index].extra_ranges || []), { open_time: '17:00', close_time: '22:00' }] };
    setBusinessHours(updated);
  };

  const updateHourRange = (dayIndex, rangeIndex, field, value) => {
    const updated = [...businessHours];
    const ranges = [...(updated[dayIndex].extra_ranges || [])];
    ranges[rangeIndex] = { ...ranges[rangeIndex], [field]: value };
    updated[dayIndex] = { ...updated[dayIndex], extra_ranges: ranges };
    setBusinessHours(updated);
  };

  const removeHourRange = (dayIndex, rangeIndex) => {
    const updated = [...businessHours];
    const ranges = [...(updated[dayIndex].extra_ranges || [])];
    ranges.splice(rangeIndex, 1);
    updated[dayIndex] = { ...updated[dayIndex], extra_ranges: ranges };
    setBusinessHours(updated);
  };

  const addDeliveryZone = () => {
    setDeliveryZones([...deliveryZones, { name: '', fee: 0, min_order: 0, active: true }]);
  };

  const updateDeliveryZone = (index, field, value) => {
    const updated = [...deliveryZones];
    updated[index] = { ...updated[index], [field]: value };
    setDeliveryZones(updated);
  };

  const removeDeliveryZone = (index) => {
    setDeliveryZones(deliveryZones.filter((_, i) => i !== index));
  };

  // Redirect if not authenticated
  if (!isAuthenticated || !isBusinessAdmin) {
    window.location.href = '/login';
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#E9FF60] mx-auto mb-4"></div>
          <p className="text-gray-400">Cargando panel...</p>
        </div>
      </div>
    );
  }

  const statusColors = {
    pending: 'bg-yellow-500',
    confirmed: 'bg-blue-500',
    preparing: 'bg-purple-500',
    ready: 'bg-green-500',
    delivered: 'bg-gray-500',
    cancelled: 'bg-red-500'
  };

  const statusLabels = {
    pending: 'Pendiente',
    confirmed: 'Confirmado',
    preparing: 'Preparando',
    ready: 'Listo',
    delivered: 'Entregado',
    cancelled: 'Cancelado'
  };

  const dayLabels = {
    monday: 'Lunes',
    tuesday: 'Martes',
    wednesday: 'Miércoles',
    thursday: 'Jueves',
    friday: 'Viernes',
    saturday: 'Sábado',
    sunday: 'Domingo'
  };

  return (
    <div className="flex h-screen" style={{ backgroundColor: '#0a0a0a', color: '#fff' }}>
      {/* Sidebar */}
      <div className="w-64 bg-[#1a1a1a] border-r border-[#333] flex flex-col">
        <div className="p-6 border-b border-[#333]">
          <h1 className="text-xl font-black truncate" style={{ fontFamily: 'Unbounded, sans-serif', color: '#E9FF60' }}>
            {business?.name || 'Mi Negocio'}
          </h1>
          <p className="text-xs text-gray-400 mt-1">Panel de Administración</p>
        </div>
        
        <nav className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-1">
            {[
              { id: 'orders', label: 'Pedidos', icon: ShoppingBag },
              { id: 'customers', label: 'Clientes', icon: Users },
              { id: 'branding', label: 'Branding', icon: Palette },
              { id: 'categories', label: 'Categorías', icon: FolderOpen },
              { id: 'products', label: 'Productos', icon: Package },
              { id: 'hours', label: 'Horarios', icon: Clock },
              { id: 'zones', label: 'Zonas de Envío', icon: MapPin },
              { id: 'payments', label: 'Pagos', icon: CreditCard }
            ].filter(item => {
              // Respect panel_config from SuperAdmin
              const cfg = business?.panel_config;
              if (!cfg) return true; // Show all if no config
              return cfg[item.id] !== false;
            }).map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  activeSection === item.id ? 'bg-[#E9FF60] text-black' : 'text-gray-400 hover:bg-[#2a2a2a]'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-semibold">{item.label}</span>
              </button>
            ))}
          </div>
        </nav>
        
        <div className="p-4 border-t border-[#333] space-y-2">
          {business?.slug && (
            <a
              href={`/${business.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-[#2a2a2a] transition-all"
              data-testid="view-my-business"
            >
              <Eye className="w-5 h-5" />
              <span>Ver mi Negocio</span>
              <ExternalLink className="w-4 h-4 ml-auto" />
            </a>
          )}
          <button onClick={() => window.location.href = '/'} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-[#2a2a2a] transition-all">
            <Home className="w-5 h-5" />
            <span>Volver al Home</span>
          </button>
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-[#2a2a2a] transition-all" data-testid="admin-logout">
            <LogOut className="w-5 h-5" />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-[#1a1a1a] border-b border-[#333] px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold" style={{ color: '#E9FF60' }}>
                {activeSection === 'orders' && 'Pedidos'}
                {activeSection === 'customers' && 'Clientes'}
                {activeSection === 'branding' && 'Branding y Diseño'}
                {activeSection === 'categories' && 'Categorías'}
                {activeSection === 'products' && 'Productos'}
                {activeSection === 'hours' && 'Horarios de Atención'}
                {activeSection === 'zones' && 'Zonas de Envío'}
                {activeSection === 'payments' && 'Configuración de Pagos'}
              </h2>
            </div>
            {activeSection === 'orders' && (
              <Button onClick={handleExportOrders} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black">
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
            )}
            {activeSection === 'orders' && selectedOrderIds.size > 0 && (
              <Button onClick={handleBulkDeleteOrders} className="bg-red-600 hover:bg-red-700 text-white ml-2" data-testid="bulk-delete-orders-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar ({selectedOrderIds.size})
              </Button>
            )}
            {activeSection === 'customers' && (
              <Button onClick={handleExportCustomers} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black" data-testid="export-customers-btn">
                <Download className="w-4 h-4 mr-2" />
                Exportar Clientes
              </Button>
            )}
            {activeSection === 'customers' && selectedCustomerIds.size > 0 && (
              <Button onClick={handleBulkDeleteCustomers} className="bg-red-600 hover:bg-red-700 text-white ml-2" data-testid="bulk-delete-customers-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar ({selectedCustomerIds.size})
              </Button>
            )}
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-auto p-8">
          {/* ORDERS VIEW */}
          {activeSection === 'orders' && (
            <div className="space-y-6">
              {/* Summary Cards */}
              {reportsSummary && (
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4">
                    <p className="text-sm text-gray-400">Pedidos Hoy</p>
                    <p className="text-3xl font-bold" style={{ color: '#E9FF60' }}>{reportsSummary.orders_today}</p>
                  </div>
                  <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4">
                    <p className="text-sm text-gray-400">Total Pedidos</p>
                    <p className="text-3xl font-bold">{reportsSummary.total_orders}</p>
                  </div>
                  <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4">
                    <p className="text-sm text-gray-400">Ingresos Totales</p>
                    <p className="text-3xl font-bold text-green-500">${reportsSummary.total_revenue?.toLocaleString()}</p>
                  </div>
                  <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4">
                    <p className="text-sm text-gray-400">Ticket Promedio</p>
                    <p className="text-3xl font-bold">${reportsSummary.average_order_value?.toFixed(2)}</p>
                  </div>
                </div>
              )}

              {/* Orders Table */}
              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#0a0a0a]">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <input type="checkbox" checked={orders.length > 0 && selectedOrderIds.size === orders.length} onChange={toggleAllOrders} className="rounded" data-testid="select-all-orders" />
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-400">Fecha</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-400">Cliente</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-400">Items</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-400">Total</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-400">Estado</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-400">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#333]">
                      {orders.map((order) => (
                        <React.Fragment key={order.id}>
                        <tr className="hover:bg-[#2a2a2a] cursor-pointer" onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)}>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" checked={selectedOrderIds.has(order.id)} onChange={() => toggleOrderSelection(order.id)} className="rounded" data-testid={`select-order-${order.id}`} />
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {new Date(order.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium">{order.customer_name}</div>
                            <div className="text-xs text-gray-500">{order.customer_email}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-400">
                            {order.items?.length || 0} productos
                          </td>
                          <td className="px-4 py-3 font-bold" style={{ color: '#E9FF60' }}>
                            ${order.total?.toFixed(2)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[order.status]} text-white`}>
                              {statusLabels[order.status]}
                            </span>
                          </td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <select
                              value={order.status}
                              onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                              className="bg-[#0a0a0a] border border-[#333] rounded px-2 py-1 text-sm"
                            >
                              {Object.entries(statusLabels).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                        {expandedOrderId === order.id && (
                          <tr>
                            <td colSpan="7" className="px-0 py-0">
                              <div className="bg-[#0a0a0a] px-8 py-4 border-t border-b border-[#333]">
                                <table className="w-full">
                                  <thead>
                                    <tr className="text-xs text-gray-500 uppercase">
                                      <th className="text-left pb-2">Producto</th>
                                      <th className="text-center pb-2">Cantidad</th>
                                      <th className="text-right pb-2">Precio Unit.</th>
                                      <th className="text-right pb-2">Subtotal</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(order.items || []).map((item, idx) => (
                                      <tr key={idx} className="text-sm border-t border-[#222]">
                                        <td className="py-2 text-gray-300">{item.name}</td>
                                        <td className="py-2 text-center text-gray-400">{item.quantity}</td>
                                        <td className="py-2 text-right text-gray-400">${item.price?.toFixed(2)}</td>
                                        <td className="py-2 text-right font-medium" style={{ color: '#E9FF60' }}>${(item.price * item.quantity).toFixed(2)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                {order.delivery_mode && <p className="text-xs text-gray-500 mt-2">Modalidad: {order.delivery_mode === 'delivery' ? 'Delivery' : 'Take Away'}{order.delivery_address ? ` — ${order.delivery_address}` : ''}</p>}
                                {order.notes && <p className="text-xs text-gray-500 mt-1">Notas: {order.notes}</p>}
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                {orders.length === 0 && (
                  <div className="text-center py-12 text-gray-500">No hay pedidos todavía</div>
                )}
                {orders.length > 0 && orders.length < ordersTotal && (
                  <div className="px-4 py-4 border-t border-[#333] text-center bg-[#0a0a0a]">
                    <Button
                      onClick={loadMoreOrders}
                      disabled={ordersLoadingMore}
                      variant="ghost"
                      className="text-[#E9FF60] hover:text-[#f0ff80] hover:bg-[#E9FF60]/10"
                      data-testid="orders-load-more-btn"
                    >
                      {ordersLoadingMore ? (
                        <><ChevronDown className="w-4 h-4 mr-2 animate-pulse" /> Cargando...</>
                      ) : (
                        <><ChevronDown className="w-4 h-4 mr-2" /> Ver más ({orders.length}/{ordersTotal})</>
                      )}
                    </Button>
                  </div>
                )}
                {orders.length > 0 && orders.length >= ordersTotal && ordersTotal > ORDERS_PAGE_SIZE && (
                  <div className="px-4 py-3 border-t border-[#333] text-center bg-[#0a0a0a] text-xs text-gray-500" data-testid="orders-end-of-list">
                    Fin de la lista — {ordersTotal} pedido(s) en total
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CUSTOMERS VIEW */}
          {activeSection === 'customers' && (
            <div className="space-y-6">
              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#0a0a0a]">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input type="checkbox" checked={customers.length > 0 && selectedCustomerIds.size === customers.length} onChange={toggleAllCustomers} className="rounded" data-testid="select-all-customers" />
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-400">Cliente</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-400">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-400">Teléfono</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-400">Pedidos</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-400">Total Gastado</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-400">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#333]">
                    {customers.map((customer) => (
                      <tr key={customer.id} className="hover:bg-[#2a2a2a]">
                        <td className="px-4 py-3">
                          <input type="checkbox" checked={selectedCustomerIds.has(customer.id)} onChange={() => toggleCustomerSelection(customer.id)} className="rounded" data-testid={`select-customer-${customer.id}`} />
                        </td>
                        <td className="px-4 py-3 font-medium">{customer.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-400">{customer.email}</td>
                        <td className="px-4 py-3 text-sm">{customer.phone}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 rounded bg-[#E9FF60] text-black text-sm font-bold">
                            {customer.total_orders}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-bold text-green-500">${customer.total_spent?.toFixed(2)}</td>
                        <td className="px-4 py-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewCustomerOrders(customer)}
                            className="text-[#E9FF60]"
                          >
                            Ver Historial
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {customers.length === 0 && (
                  <div className="text-center py-12 text-gray-500">No hay clientes todavía</div>
                )}
              </div>

              {/* Customer Orders Modal */}
              <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
                <DialogContent className="max-w-2xl bg-[#1a1a1a] border-[#333] text-white">
                  <DialogHeader>
                    <DialogTitle style={{ color: '#E9FF60' }}>
                      Historial de {selectedCustomer?.name}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                    {customerOrders.map((order) => (
                      <div key={order.id} className="bg-[#0a0a0a] rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className="text-sm text-gray-400">
                              {new Date(order.created_at).toLocaleDateString('es-AR')}
                            </span>
                            <span className={`ml-2 px-2 py-1 rounded-full text-xs ${statusColors[order.status]} text-white`}>
                              {statusLabels[order.status]}
                            </span>
                          </div>
                          <span className="font-bold" style={{ color: '#E9FF60' }}>${order.total?.toFixed(2)}</span>
                        </div>
                        <div className="text-sm text-gray-400">
                          {order.items?.map(item => `${item.name} x${item.quantity}`).join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* BRANDING VIEW */}
          {activeSection === 'branding' && (
            <div className="space-y-6 max-w-4xl">
              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Información Básica</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">Nombre del Negocio</label>
                    <Input value={brandingForm.name} onChange={(e) => setBrandingForm({ ...brandingForm, name: e.target.value })} className="bg-[#0a0a0a] border-[#333] text-white" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">WhatsApp</label>
                    <Input value={brandingForm.whatsapp} onChange={(e) => setBrandingForm({ ...brandingForm, whatsapp: e.target.value })} className="bg-[#0a0a0a] border-[#333] text-white" />
                  </div>
                </div>
              </div>

              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Colores</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {[
                    { key: 'primary_color', label: 'Primario' },
                    { key: 'accent_color', label: 'Acento' },
                    { key: 'background_color', label: 'Fondo' },
                    { key: 'text_color', label: 'Texto' },
                    { key: 'card_color', label: 'Tarjetas' }
                  ].map((color) => (
                    <div key={color.key}>
                      <label className="block text-sm font-medium mb-2 text-gray-300">{color.label}</label>
                      <div className="flex gap-2">
                        <input type="color" value={brandingForm[color.key]} onChange={(e) => setBrandingForm({ ...brandingForm, [color.key]: e.target.value })} className="w-12 h-10 rounded cursor-pointer" />
                        <Input type="text" value={brandingForm[color.key]} onChange={(e) => setBrandingForm({ ...brandingForm, [color.key]: e.target.value })} className="flex-1 bg-[#0a0a0a] border-[#333] text-white font-mono text-xs" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Tipografía</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">Fuente de Títulos</label>
                    <select 
                      value={brandingForm.font_heading} 
                      onChange={(e) => setBrandingForm({ ...brandingForm, font_heading: e.target.value })} 
                      className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#333] rounded-md text-white"
                      style={{ fontFamily: brandingForm.font_heading }}
                      data-testid="font-heading-select"
                    >
                      {availableFonts.map(f => (
                        <option key={f.id || f.name} value={f.name} style={{ fontFamily: f.name }}>
                          {f.name}{f.source === 'custom' ? ' (Custom)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">Fuente de Textos</label>
                    <select 
                      value={brandingForm.font_body} 
                      onChange={(e) => setBrandingForm({ ...brandingForm, font_body: e.target.value })} 
                      className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#333] rounded-md text-white"
                      style={{ fontFamily: brandingForm.font_body }}
                      data-testid="font-body-select"
                    >
                      {availableFonts.map(f => (
                        <option key={f.id || f.name} value={f.name} style={{ fontFamily: f.name }}>
                          {f.name}{f.source === 'custom' ? ' (Custom)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Font preview */}
                <div className="mt-4 p-4 bg-[#0a0a0a] rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">Vista previa:</p>
                  <p className="text-xl font-bold" style={{ fontFamily: brandingForm.font_heading }}>
                    {business?.name || 'Tu Restaurante'} — Título
                  </p>
                  <p className="text-sm mt-1 text-gray-400" style={{ fontFamily: brandingForm.font_body }}>
                    Menú del día • Pedidos a domicilio • El mejor sabor de la ciudad
                  </p>
                </div>
              </div>

              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Imágenes</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">Logo</label>
                    <Input type="file" accept="image/*" onChange={(e) => handleImageUpload(e.target.files[0], 'logo_url')} className="bg-[#0a0a0a] border-[#333] text-white" />
                    {brandingForm.logo_url && <img src={brandingForm.logo_url} alt="Logo" className="h-20 w-20 object-cover rounded-lg mt-2" />}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">Imagen Hero</label>
                    <Input type="file" accept="image/*" onChange={(e) => handleImageUpload(e.target.files[0], 'hero_image')} className="bg-[#0a0a0a] border-[#333] text-white" data-testid="hero-image-upload" />
                    <p className="text-xs text-gray-500 mt-1">Recomendamos imágenes horizontales (formato 16:9). Subí la foto en su orientación correcta antes de subirla.</p>
                    {brandingForm.hero_image && <img src={brandingForm.hero_image} alt="Hero" className="h-20 w-32 object-cover rounded-lg mt-2" />}
                  </div>
                </div>
              </div>

              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Enlace Personalizado</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">Texto del botón</label>
                    <Input placeholder="Ej: Reservar mesa" value={brandingForm.custom_button_text} onChange={(e) => setBrandingForm({ ...brandingForm, custom_button_text: e.target.value })} className="bg-[#0a0a0a] border-[#333] text-white" data-testid="custom-button-text" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">URL del enlace</label>
                    <Input placeholder="https://..." value={brandingForm.custom_button_url} onChange={(e) => setBrandingForm({ ...brandingForm, custom_button_url: e.target.value })} className="bg-[#0a0a0a] border-[#333] text-white" data-testid="custom-button-url" />
                  </div>
                </div>
              </div>

              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Redes Sociales</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">Instagram (URL completa)</label>
                    <Input placeholder="https://instagram.com/tunegocio" value={brandingForm.social_instagram} onChange={(e) => setBrandingForm({ ...brandingForm, social_instagram: e.target.value })} className="bg-[#0a0a0a] border-[#333] text-white" data-testid="social-instagram" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">Facebook (URL completa)</label>
                    <Input placeholder="https://facebook.com/tunegocio" value={brandingForm.social_facebook} onChange={(e) => setBrandingForm({ ...brandingForm, social_facebook: e.target.value })} className="bg-[#0a0a0a] border-[#333] text-white" data-testid="social-facebook" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">WhatsApp se toma del número configurado arriba.</p>
              </div>

              <Button onClick={handleSaveBranding} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold">
                <Save className="w-4 h-4 mr-2" />
                Guardar Cambios
              </Button>
            </div>
          )}

          {/* CATEGORIES VIEW */}
          {activeSection === 'categories' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  {categories.length > 0 && (
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-400">
                      <input type="checkbox" checked={categories.length > 0 && selectedCategoryIds.size === categories.length} onChange={toggleAllCategories} className="rounded" data-testid="select-all-categories" />
                      Seleccionar todo
                    </label>
                  )}
                  {selectedCategoryIds.size > 0 && (
                    <Button onClick={handleBulkDeleteCategories} className="bg-red-600 hover:bg-red-700 text-white" size="sm" data-testid="bulk-delete-categories-btn">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar ({selectedCategoryIds.size})
                    </Button>
                  )}
                </div>
                <Button onClick={() => { setCategoryForm({ name: '', order: categories.length, hidden: false }); setCategoryDialog({ open: true, data: null }); }} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Categoría
                </Button>
              </div>

              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg overflow-hidden">
                {categories.map((category) => (
                  <div key={category.id} className="flex justify-between items-center p-4 border-b border-[#333] hover:bg-[#2a2a2a]">
                    <div className="flex items-center gap-4">
                      <input type="checkbox" checked={selectedCategoryIds.has(category.id)} onChange={() => toggleCategorySelection(category.id)} className="rounded" data-testid={`select-category-${category.id}`} />
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold" style={{ backgroundColor: '#E9FF60', color: '#0a0a0a' }}>
                        {category.order + 1}
                      </div>
                      <div>
                        <span className="font-semibold">{category.name}</span>
                        {category.hidden && <span className="ml-2 px-2 py-1 text-xs rounded bg-gray-700">Oculta</span>}
                        <div className="text-sm text-gray-500">{products.filter(p => p.category_id === category.id).length} productos</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setCategoryForm({ name: category.name, order: category.order, hidden: category.hidden || false }); setCategoryDialog({ open: true, data: category }); }} className="text-[#E9FF60]">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteCategory(category.id)} className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {categories.length === 0 && <div className="text-center py-12 text-gray-500">No hay categorías</div>}
              </div>
            </div>
          )}

          {/* PRODUCTS VIEW */}
          {activeSection === 'products' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div className="inline-flex bg-[#1a1a1a] border border-[#333] rounded-lg p-1" data-testid="products-view-toggle">
                  <button
                    onClick={() => setProductsView('cards')}
                    className={`px-4 py-1.5 text-sm rounded transition ${productsView === 'cards' ? 'bg-[#E9FF60] text-black font-bold' : 'text-gray-400 hover:text-white'}`}
                    data-testid="view-cards-btn"
                  >Tarjetas</button>
                  <button
                    onClick={() => setProductsView('bulk')}
                    className={`px-4 py-1.5 text-sm rounded transition ${productsView === 'bulk' ? 'bg-[#E9FF60] text-black font-bold' : 'text-gray-400 hover:text-white'}`}
                    data-testid="view-bulk-btn"
                  >Gestión Masiva</button>
                </div>
                <Button onClick={() => { setProductForm({ category_id: categories[0]?.id || '', name: '', description: '', price: 0, image_url: '', available: true, is_popular: false, discount_price: null, stock: null }); setProductDialog({ open: true, data: null }); }} disabled={categories.length === 0} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Producto
                </Button>
              </div>

              {productsView === 'bulk' ? (
                <ProductsBulkTable
                  categories={categories}
                  onAfterSave={loadData}
                  onBulkDelete={loadData}
                />
              ) : (
              <>
              <div className="flex items-center gap-3">
                {products.length > 0 && (
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-400">
                      <input type="checkbox" checked={products.length > 0 && selectedProductIds.size === products.length} onChange={toggleAllProducts} className="rounded" data-testid="select-all-products" />
                      Seleccionar todo
                    </label>
                  )}
                  {selectedProductIds.size > 0 && (
                    <Button onClick={handleBulkDeleteProducts} className="bg-red-600 hover:bg-red-700 text-white" size="sm" data-testid="bulk-delete-products-btn">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar ({selectedProductIds.size})
                    </Button>
                  )}
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((product) => (
                  <div key={product.id} className={`bg-[#1a1a1a] border rounded-lg overflow-hidden hover:border-[#E9FF60]/50 ${selectedProductIds.has(product.id) ? 'border-red-500' : 'border-[#333]'}`}>
                    <div className="flex items-center gap-2 px-4 pt-3">
                      <input type="checkbox" checked={selectedProductIds.has(product.id)} onChange={() => toggleProductSelection(product.id)} className="rounded" data-testid={`select-product-${product.id}`} />
                      <span className="text-xs text-gray-500">Seleccionar</span>
                    </div>
                    {product.image_url && <img src={product.image_url} alt={product.name} className="w-full h-32 object-cover" />}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h4 className="font-semibold" style={{ color: '#E9FF60' }}>{product.name}</h4>
                        {product.is_popular && <span className="px-2 py-1 text-xs rounded bg-[#E9FF60] text-black font-bold">Popular</span>}
                      </div>
                      <p className="text-sm text-gray-500 mb-1">{categories.find(c => c.id === product.category_id)?.name}</p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-lg font-bold" style={{ color: '#E9FF60' }}>${product.price?.toFixed(2)}</span>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setProductForm({ category_id: product.category_id, name: product.name, description: product.description || '', price: product.price, image_url: product.image_url || '', available: product.available, is_popular: product.is_popular || false, discount_price: product.discount_price || null, stock: product.stock !== undefined ? product.stock : null }); setProductDialog({ open: true, data: product }); }} className="text-[#E9FF60]">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteProduct(product.id)} className="text-red-500">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {products.length === 0 && <div className="bg-[#1a1a1a] border border-[#333] rounded-lg text-center py-12 text-gray-500">{categories.length === 0 ? 'Creá una categoría primero' : 'No hay productos'}</div>}
              </>
              )}
            </div>
          )}

          {/* HOURS VIEW */}
          {activeSection === 'hours' && (
            <div className="space-y-6 max-w-2xl">
              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Horarios de Atención</h3>
                <div className="space-y-3">
                  {businessHours.map((hour, index) => (
                    <div key={hour.day} className="p-3 bg-[#0a0a0a] rounded-lg">
                      {/* Main range row */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="w-24 font-medium text-sm">{dayLabels[hour.day]}</div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={hour.is_open} onChange={(e) => updateBusinessHour(index, 'is_open', e.target.checked)} className="rounded" />
                          <span className="text-sm">Abierto</span>
                        </label>
                        {hour.is_open && (
                          <>
                            <Input type="time" value={hour.open_time} onChange={(e) => updateBusinessHour(index, 'open_time', e.target.value)} className="w-30 bg-[#1a1a1a] border-[#333] text-white" />
                            <span className="text-gray-500">a</span>
                            <Input type="time" value={hour.close_time} onChange={(e) => updateBusinessHour(index, 'close_time', e.target.value)} className="w-30 bg-[#1a1a1a] border-[#333] text-white" />
                            <button
                              onClick={() => addHourRange(index)}
                              className="w-7 h-7 rounded-full bg-[#E9FF60] text-black font-bold text-base flex items-center justify-center hover:bg-[#f0ff80] transition-colors flex-shrink-0"
                              title="Agregar otro rango horario"
                              data-testid={`add-range-${hour.day}`}
                            >+</button>
                          </>
                        )}
                      </div>
                      {/* Extra ranges */}
                      {hour.is_open && (hour.extra_ranges || []).map((range, rIndex) => (
                        <div key={rIndex} className="flex items-center gap-3 mt-2 ml-28 flex-wrap">
                          <span className="text-xs text-gray-500 w-6">+</span>
                          <Input type="time" value={range.open_time} onChange={(e) => updateHourRange(index, rIndex, 'open_time', e.target.value)} className="w-30 bg-[#1a1a1a] border-[#444] text-white" />
                          <span className="text-gray-500 text-sm">a</span>
                          <Input type="time" value={range.close_time} onChange={(e) => updateHourRange(index, rIndex, 'close_time', e.target.value)} className="w-30 bg-[#1a1a1a] border-[#444] text-white" />
                          <button
                            onClick={() => removeHourRange(index, rIndex)}
                            className="text-red-400 hover:text-red-300 text-lg font-bold px-1 transition-colors"
                            title="Eliminar rango"
                            data-testid={`remove-range-${hour.day}-${rIndex}`}
                          >×</button>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Tiempo Estimado de Entrega</h3>
                <Input value={settingsForm.estimated_delivery_time} onChange={(e) => setSettingsForm({ ...settingsForm, estimated_delivery_time: e.target.value })} placeholder="30-45 min" className="bg-[#0a0a0a] border-[#333] text-white max-w-xs" />
              </div>

              <Button onClick={handleSaveSettings} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold">
                <Save className="w-4 h-4 mr-2" />
                Guardar Horarios
              </Button>
            </div>
          )}

          {/* ZONES VIEW */}
          {activeSection === 'zones' && (
            <div className="space-y-6 max-w-3xl">
              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold" style={{ color: '#E9FF60' }}>Zonas de Envío</h3>
                  <Button onClick={addDeliveryZone} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black">
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Zona
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {deliveryZones.map((zone, index) => (
                    <div key={index} className="flex items-center gap-4 p-4 bg-[#0a0a0a] rounded-lg">
                      <Input value={zone.name} onChange={(e) => updateDeliveryZone(index, 'name', e.target.value)} placeholder="Nombre de zona" className="flex-1 bg-[#1a1a1a] border-[#333] text-white" />
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-400">Costo:</span>
                        <Input type="number" value={zone.fee} onChange={(e) => updateDeliveryZone(index, 'fee', parseFloat(e.target.value) || 0)} className="w-24 bg-[#1a1a1a] border-[#333] text-white" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-400">Mín:</span>
                        <Input type="number" value={zone.min_order} onChange={(e) => updateDeliveryZone(index, 'min_order', parseFloat(e.target.value) || 0)} className="w-24 bg-[#1a1a1a] border-[#333] text-white" />
                      </div>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={zone.active} onChange={(e) => updateDeliveryZone(index, 'active', e.target.checked)} className="rounded" />
                        <span className="text-sm">Activa</span>
                      </label>
                      <Button variant="ghost" size="sm" onClick={() => removeDeliveryZone(index)} className="text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {deliveryZones.length === 0 && <p className="text-center text-gray-500 py-4">No hay zonas configuradas</p>}
                </div>
              </div>

              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Opciones de Entrega</h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 bg-[#0a0a0a] rounded-lg cursor-pointer">
                    <input type="checkbox" checked={settingsForm.accepts_delivery} onChange={(e) => setSettingsForm({ ...settingsForm, accepts_delivery: e.target.checked })} className="rounded" />
                    <span>Acepta Delivery</span>
                  </label>
                  <label className="flex items-center gap-3 p-3 bg-[#0a0a0a] rounded-lg cursor-pointer">
                    <input type="checkbox" checked={settingsForm.accepts_takeaway} onChange={(e) => setSettingsForm({ ...settingsForm, accepts_takeaway: e.target.checked })} className="rounded" />
                    <span>Acepta Take Away (Retiro en Local)</span>
                  </label>
                  <div className="p-3 bg-[#0a0a0a] rounded-lg">
                    <label className="block text-sm mb-2">Pedido Mínimo ($)</label>
                    <Input type="number" value={settingsForm.min_order_amount} onChange={(e) => setSettingsForm({ ...settingsForm, min_order_amount: parseFloat(e.target.value) || 0 })} className="w-32 bg-[#1a1a1a] border-[#333] text-white" />
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveSettings} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold">
                <Save className="w-4 h-4 mr-2" />
                Guardar Configuración
              </Button>
            </div>
          )}

          {/* PAYMENTS VIEW */}
          {activeSection === 'payments' && (
            <div className="space-y-6 max-w-2xl">
              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Métodos de Pago</h3>
                <div className="space-y-3">
                  {['cash', 'transfer', 'mercadopago'].map((method) => (
                    <label key={method} className="flex items-center gap-3 p-3 bg-[#0a0a0a] rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settingsForm.payment_methods?.includes(method)}
                        onChange={(e) => {
                          const methods = settingsForm.payment_methods || [];
                          if (e.target.checked) {
                            setSettingsForm({ ...settingsForm, payment_methods: [...methods, method] });
                          } else {
                            setSettingsForm({ ...settingsForm, payment_methods: methods.filter(m => m !== method) });
                          }
                        }}
                        className="rounded"
                      />
                      <span className="capitalize">
                        {method === 'cash' ? 'Efectivo' : method === 'transfer' ? 'Transferencia Bancaria' : 'Mercado Pago'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Integración Mercado Pago</h3>
                <p className="text-sm text-gray-400 mb-4">Configurá tus credenciales de Mercado Pago para recibir pagos directos.</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">Public Key</label>
                    <Input
                      value={settingsForm.mercadopago_public_key}
                      onChange={(e) => setSettingsForm({ ...settingsForm, mercadopago_public_key: e.target.value })}
                      placeholder="APP_USR-xxxxxxxx"
                      className="bg-[#0a0a0a] border-[#333] text-white font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-300">Access Token</label>
                    <Input
                      type="password"
                      value={settingsForm.mercadopago_access_token}
                      onChange={(e) => setSettingsForm({ ...settingsForm, mercadopago_access_token: e.target.value })}
                      placeholder="APP_USR-xxxxxxxx"
                      className="bg-[#0a0a0a] border-[#333] text-white font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">El token se guarda de forma segura y nunca se muestra</p>
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveSettings} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold">
                <Save className="w-4 h-4 mr-2" />
                Guardar Configuración de Pagos
              </Button>
            </div>
          )}
        </main>
      </div>

      {/* Category Dialog */}
      <Dialog open={categoryDialog.open} onOpenChange={(open) => setCategoryDialog({ ...categoryDialog, open })}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle style={{ color: '#E9FF60' }}>{categoryDialog.data ? 'Editar Categoría' : 'Nueva Categoría'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Nombre</label>
              <Input value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} className="bg-[#0a0a0a] border-[#333] text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Orden</label>
              <Input type="number" value={categoryForm.order} onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) })} className="bg-[#0a0a0a] border-[#333] text-white" />
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={categoryForm.hidden} onChange={(e) => setCategoryForm({ ...categoryForm, hidden: e.target.checked })} className="rounded" />
              <span className="text-sm text-gray-300">Ocultar categoría</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialog({ open: false, data: null })} className="border-[#333] text-white">Cancelar</Button>
            <Button onClick={handleSaveCategory} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={productDialog.open} onOpenChange={(open) => setProductDialog({ ...productDialog, open })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle style={{ color: '#E9FF60' }}>{productDialog.data ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Categoría</label>
              <select value={productForm.category_id} onChange={(e) => setProductForm({ ...productForm, category_id: e.target.value })} className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#333] rounded-md text-white">
                {categories.map((cat) => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Nombre</label>
              <Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} className="bg-[#0a0a0a] border-[#333] text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Descripción</label>
              <Textarea value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} rows={3} className="bg-[#0a0a0a] border-[#333] text-white" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Precio</label>
                <Input type="number" step="0.01" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: parseFloat(e.target.value) || 0 })} className="bg-[#0a0a0a] border-[#333] text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Precio con Descuento</label>
                <Input type="number" step="0.01" value={productForm.discount_price || ''} onChange={(e) => setProductForm({ ...productForm, discount_price: e.target.value ? parseFloat(e.target.value) : null })} className="bg-[#0a0a0a] border-[#333] text-white" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Imagen</label>
              <div className="flex gap-4 items-center">
                <Input type="file" accept="image/*" onChange={(e) => handleImageUpload(e.target.files[0], 'product')} className="bg-[#0a0a0a] border-[#333] text-white" />
                {productForm.image_url && <img src={productForm.image_url} alt="Preview" className="h-16 w-16 object-cover rounded" />}
              </div>
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={productForm.available} onChange={(e) => setProductForm({ ...productForm, available: e.target.checked })} className="rounded" />
                <span className="text-sm text-gray-300">Disponible</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={productForm.is_popular} onChange={(e) => setProductForm({ ...productForm, is_popular: e.target.checked })} className="rounded" />
                <span className="text-sm text-gray-300">Popular</span>
              </label>
            </div>
            {/* Stock */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">Stock</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={productForm.stock === null}
                    onChange={(e) => setProductForm({ ...productForm, stock: e.target.checked ? null : 0 })}
                    className="rounded"
                    data-testid="stock-unlimited-toggle"
                  />
                  <span className="text-sm text-[#E9FF60] font-medium">Sin límite (∞)</span>
                </label>
                {productForm.stock !== null && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      value={productForm.stock ?? 0}
                      onChange={(e) => setProductForm({ ...productForm, stock: Math.max(0, parseInt(e.target.value) || 0) })}
                      className="bg-[#0a0a0a] border-[#333] text-white w-28"
                      placeholder="Ej: 10"
                      data-testid="stock-input"
                    />
                    <span className="text-xs text-gray-500">unidades</span>
                    {productForm.stock === 0 && (
                      <span className="text-xs text-red-400 font-medium">Agotado</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialog({ open: false, data: null })} className="border-[#333] text-white">Cancelar</Button>
            <Button onClick={handleSaveProduct} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
